import React from "react";
import Icon from "../AppIcon";
import Input from "../ui/Input";

/**
 * Universal Options Panel
 * Shows configurable options available for ALL distribution strategies:
 * - Minimum Floor - guarantee minimum amount per employee
 * - Hour Factor - weight distribution by hours worked
 * - Seniority Factor - weight distribution by length of service
 */
const UniversalOptionsPanel = ({
  options = {},
  onChange,
  readonly = false,
  onConfigureSeniority,
  className = "",
}) => {
  const {
    enableMinimumFloor = false,
    minimumFloorAmount = 0,
    enableHourFactor = false,
    hourFactorWeight = 0.5,
    enableSeniorityFactor = false,
    seniorityFactorWeight = 0.3,
    seniorityBands = [],
  } = options;

  const handleToggle = (field) => (e) => {
    const checked = e.target.checked;
    onChange({ ...options, [field]: checked });
  };

  const handleChange = (field) => (e) => {
    const value = field.includes("Weight") 
  ? parseFloat(e.target.value) / 100 
      : parseFloat(e.target.value);
    onChange({ ...options, [field]: isNaN(value) ? 0 : value });
  };

  const handleSliderChange = (field) => (e) => {
    const value = parseFloat(e.target.value);
    onChange({ ...options, [field]: value });
  };

  return (
    <div className={`space-y-4 p-4 bg-muted/30 rounded-lg border border-border ${className}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon name="Sliders" size={18} className="text-primary" />
        <h4 className="font-semibold">Distribution Options</h4>
        <span className="text-xs text-muted-foreground">(Apply to any strategy)</span>
      </div>

      {/* Minimum Floor */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
    <label className="relative inline-flex items-center cursor-pointer">
 <input
   type="checkbox"
      checked={enableMinimumFloor}
    onChange={handleToggle("enableMinimumFloor")}
      disabled={readonly}
className="sr-only peer"
       />
    <div className="w-9 h-5 bg-border rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
     </label>
            <div>
<span className="font-medium text-sm">Minimum Floor</span>
         <p className="text-xs text-muted-foreground">
        Guarantee minimum amount per employee
   </p>
        </div>
 </div>
          <Icon 
      name="Shield" 
 size={18} 
            className={enableMinimumFloor ? "text-primary" : "text-muted-foreground"} 
  />
        </div>
        
        {enableMinimumFloor && (
          <div className="ml-11 mt-2">
      <div className="flex items-center gap-2">
   <span className="text-sm text-muted-foreground">&pound;</span>
              <Input
             type="number"
                value={minimumFloorAmount || ""}
     onChange={handleChange("minimumFloorAmount")}
   placeholder="0.00"
     min={0}
step={0.01}
          disabled={readonly}
  className="w-32"
       />
       <span className="text-sm text-muted-foreground">per employee</span>
  </div>
       <p className="text-xs text-muted-foreground mt-1">
         Each employee receives at least this amount. Remainder distributed by strategy.
       </p>
 </div>
 )}
 </div>

      {/* Hour Factor */}
      <div className="space-y-2 pt-2 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <label className="relative inline-flex items-center cursor-pointer">
         <input
      type="checkbox"
    checked={enableHourFactor}
      onChange={handleToggle("enableHourFactor")}
              disabled={readonly}
      className="sr-only peer"
      />
       <div className="w-9 h-5 bg-border rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
            </label>
 <div>
        <span className="font-medium text-sm">Hour Factor</span>
              <p className="text-xs text-muted-foreground">
        Weight distribution by hours worked
    </p>
            </div>
          </div>
          <Icon 
   name="Clock" 
          size={18} 
     className={enableHourFactor ? "text-primary" : "text-muted-foreground"} 
          />
    </div>
        
        {enableHourFactor && (
          <div className="ml-11 mt-2 space-y-2">
            <div className="p-2 bg-info/10 border border-info/20 rounded text-xs text-info flex items-start gap-2">
              <Icon name="Info" size={14} className="shrink-0 mt-0.5" />
              <span>
                Each employee receives a <strong>score of 1.0–2.0</strong> based on their hours worked relative to the group. The employee with the most hours scores 2.0; the least hours scores 1.0. Requires clock records from HR integration or manual entry.
              </span>
            </div>
          </div>
        )}
      </div>

 {/* Seniority Factor */}
      <div className="space-y-2 pt-2 border-t border-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
     <label className="relative inline-flex items-center cursor-pointer">
       <input
     type="checkbox"
          checked={enableSeniorityFactor}
        onChange={handleToggle("enableSeniorityFactor")}
         disabled={readonly}
  className="sr-only peer"
      />
       <div className="w-9 h-5 bg-border rounded-full peer peer-checked:bg-primary transition-colors after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:after:translate-x-4"></div>
            </label>
            <div>
         <span className="font-medium text-sm">Seniority Factor</span>
    <p className="text-xs text-muted-foreground">
Weight distribution by length of service
    </p>
            </div>
          </div>
          <Icon 
       name="Award" 
      size={18} 
       className={enableSeniorityFactor ? "text-primary" : "text-muted-foreground"} 
          />
        </div>
   
        {enableSeniorityFactor && (
   <div className="ml-11 mt-2 space-y-3">
            <div className="p-2 bg-info/10 border border-info/20 rounded text-xs text-info flex items-start gap-2">
              <Icon name="Info" size={14} className="shrink-0 mt-0.5" />
              <span>
                Each employee receives a <strong>score of 1.0–2.0</strong> based on their length of service using the configured seniority bands.
              </span>
            </div>
            <div className="hidden"><input
    type="range"
                min={0}
         max={1}
 step={0.1}
    value={seniorityFactorWeight}
        onChange={handleSliderChange("seniorityFactorWeight")}
    disabled={readonly}
     className="flex-1 h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
  /></div>
            
            {/* Seniority Bands Preview */}
    {seniorityBands && seniorityBands.length > 0 && (
  <div className="p-2 bg-muted rounded border border-border">
       <p className="text-xs font-medium mb-1">Configured Bands:</p>
   <div className="text-xs text-muted-foreground space-y-0.5">
          {seniorityBands.map((band, i) => (
        <div key={i} className="flex justify-between">
        <span>{band.name}: {band.minMonths}{band.maxMonths ? `-${band.maxMonths}` : "+"} months</span>
 <span className="font-mono">{band.multiplier.toFixed(1)}&times;</span>
           </div>
 ))}
  </div>
   </div>
            )}
            
            {/* Configure Button */}
        {onConfigureSeniority && !readonly && (
  <button
                type="button"
      onClick={onConfigureSeniority}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
       >
     <Icon name="Settings" size={14} />
       Configure Seniority Bands
  </button>
     )}
    </div>
        )}
      </div>

  {/* Summary when options enabled */}
    {(enableMinimumFloor || enableHourFactor || enableSeniorityFactor) && (
        <div className="pt-3 border-t border-border">
      <p className="text-xs text-muted-foreground">
      <strong>Applied options:</strong>{" "}
            {[
              enableHourFactor && "Hours (1–2×)",
        enableSeniorityFactor && "Seniority (1–2×)",
      enableMinimumFloor && `Min Floor (£${minimumFloorAmount || 0})`,
            ].filter(Boolean).join(" → ")}
     </p>
        </div>
      )}
    </div>
  );
};

export default UniversalOptionsPanel;
